import { useEffect, useSyncExternalStore } from 'react';

/*
 * Capability gate категорий правил (ТЗ FRONT BM RULES v2, §0, §6).
 *
 * Пока backend не знает про category / business_metric_key /
 * business_lifecycle, core DTO правила закрыт (additionalProperties: false)
 * и лишнее поле дало бы 422. Поэтому категории включаются только тогда, когда
 * backend сам их отдаёт: в ответе списка или карточки правила V2 у правила
 * есть поле category. Никакого флага и никакой догадки — после выкладки
 * backend всё включится само.
 *
 * Состояния: unknown (ответов с правилами ещё не было), supported, unsupported.
 * Пустой список ничего не доказывает и состояние не меняет.
 */

export const SUPPORT_UNKNOWN = 'unknown';
export const SUPPORT_YES = 'supported';
export const SUPPORT_NO = 'unsupported';

let state = SUPPORT_UNKNOWN;
let probe = null;
const listeners = new Set();

const setState = (next) => {
  if (next === state) return;
  state = next;
  listeners.forEach((listener) => listener());
};

/** Вызывается сервисом на каждый ответ с правилами V2. */
export const noteRulesShape = (rules) => {
  const list = (Array.isArray(rules) ? rules : [rules]).filter((rule) => rule && typeof rule === 'object');
  if (list.length === 0) return;
  // Поддержка не «откатывается» одним старым ответом: раз увидели — значит есть.
  if (list.some((rule) => Object.prototype.hasOwnProperty.call(rule, 'category'))) setState(SUPPORT_YES);
  else if (state !== SUPPORT_YES) setState(SUPPORT_NO);
};

export const getRuleCategorySupport = () => state;

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Только для тестов. */
export const resetRuleCategorySupport = () => {
  state = SUPPORT_UNKNOWN;
  probe = null;
  listeners.forEach((listener) => listener());
};

/**
 * Хук для формы и списка. Если ответов с правилами ещё не было (форма
 * создания открыта со страницы без списка), один раз запрашивает одно
 * правило, чтобы узнать форму ответа. probeFn передаётся снаружи, чтобы
 * этот модуль не зависел от services/violations.
 */
export const useRuleCategorySupport = (probeFn) => {
  const current = useSyncExternalStore(subscribe, getRuleCategorySupport, getRuleCategorySupport);
  useEffect(() => {
    if (current !== SUPPORT_UNKNOWN || !probeFn || probe) return;
    // Один пробный запрос на сессию: пустой список ответа не меняет, и
    // повторять его при каждом открытии формы бессмысленно.
    probe = Promise.resolve()
      .then(() => probeFn())
      .catch(() => {});
  }, [current, probeFn]);
  return current === SUPPORT_YES;
};
